import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usersApi, skillsApi, socialApi } from '../utils/api';
import GlassCard from '../components/GlassCard';
import SkillCard from '../components/SkillCard';
import { CategoryModal } from '../components/CategoryModal';
import ConfirmModal from '../components/ConfirmModal';
import StarButton from '../components/StarButton';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { ArrowLeft, Settings, Eye, Star, Code, Download, MapPin, Link as LinkIcon, Github, Linkedin, BookOpen, MessageCircle, Users } from 'lucide-react';
import { toast } from 'sonner';

const ProfilePage = () => {
  const { id } = useParams();
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [skills, setSkills] = useState([]);
  const [stats, setStats] = useState({ skills: 0, stars: 0, views: 0, installs: 0 });
  const [isFollowing, setIsFollowing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [skillToEditCategory, setSkillToEditCategory] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [skillToDelete, setSkillToDelete] = useState(null);

  const isOwnProfile = currentUser?.id === id;

  useEffect(() => {
    loadProfile();
  }, [id]);

  const loadProfile = async () => {
    try {
      setLoading(true);
      const [userRes, skillsRes] = await Promise.all([
        usersApi.get(id),
        skillsApi.list({ user_id: id }),
      ]);
      
      setProfile(userRes.data);
      
      // Profile page ALWAYS shows only PUBLIC published skills
      // (regardless of whether it's your own profile or someone else's)
      // Use My Skills page to see all your skills including drafts
      const filteredSkills = skillsRes.data.filter(skill => 
        skill.visibility === 'PUBLIC' && skill.is_published
      );
      
      setSkills(filteredSkills);
      
      // Calculate stats from filtered skills
      const totalStats = filteredSkills.reduce(
        (acc, skill) => ({
          skills: acc.skills + 1,
          stars: acc.stars + (skill.star_count || 0),
          views: acc.views + (skill.view_count || 0),
          installs: acc.installs + (skill.install_count || 0),
        }),
        { skills: 0, stars: 0, views: 0, installs: 0 }
      );
      setStats(totalStats);

      // Check if following
      if (currentUser && !isOwnProfile) {
        const followRes = await socialApi.isFollowing(id);
        setIsFollowing(followRes.data.is_following);
      }
    } catch (error) {
      console.error('Failed to load profile', error);
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleFollow = async () => {
    try {
      if (isFollowing) {
        await socialApi.unfollow(id);
        setIsFollowing(false);
        toast.success('Unfollowed');
      } else {
        await socialApi.follow(id);
        setIsFollowing(true);
        toast.success('Following!');
      }
    } catch (error) {
      toast.error('Failed to update follow status');
    }
  };

  const handleEditSkill = (skill) => {
    navigate(`/skills/${skill.id}/edit`);
  };

  const handleDeleteSkill = (skill) => {
    setSkillToDelete(skill);
    setDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!skillToDelete) return;
    try {
      await skillsApi.delete(skillToDelete.id);
      setSkills(skills.filter(s => s.id !== skillToDelete.id));
      toast.success('Skill deleted successfully');
      setDeleteModalOpen(false);
      setSkillToDelete(null);
      // Reload profile to update stats
      loadProfile();
    } catch (error) {
      console.error('Failed to delete skill', error);
      toast.error('Failed to delete skill');
    }
  };

  const handleCategoryChange = (skill) => {
    setSkillToEditCategory(skill);
    setCategoryModalOpen(true);
  };

  const handleSaveCategory = async (categoryId) => {
    if (!skillToEditCategory) return;
    try {
      await skillsApi.update(skillToEditCategory.id, { category_id: categoryId });
      setSkills(skills.map(s =>
        s.id === skillToEditCategory.id ? { ...s, category_id: categoryId } : s
      ));
      toast.success('Category updated');
      setCategoryModalOpen(false);
      setSkillToEditCategory(null);
    } catch (error) {
      console.error('Failed to update category', error);
      toast.error('Failed to update category');
    }
  };

  const handleUpdateVisibility = async (skillId, visibility) => {
    try {
      await skillsApi.update(skillId, { visibility });
      setSkills(skills.map(s =>
        s.id === skillId ? { ...s, visibility } : s
      ));
      toast.success(`Visibility updated to ${visibility}`);
    } catch (error) {
      console.error('Failed to update visibility', error);
      toast.error('Failed to update visibility');
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <GlassCard className="h-64 skeleton"></GlassCard>
        <GlassCard className="h-96 skeleton"></GlassCard>
      </div>
    );
  }

  if (!profile) return null;

  return (
    <div className="space-y-6">
      {/* Back Button */}
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-text-gray dark:text-dark-text-secondary hover:text-text-dark dark:hover:text-dark-text transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
        Back
      </button>

      {/* Optimized Profile Header */}
      <GlassCard className="overflow-hidden p-0">
        {/* Cover Photo */}
        <div className="h-48 bg-gradient-to-r from-primary/30 via-primary/40 to-primary-dark/30 relative">
          {profile.cover_photo_url ? (
            <img 
              src={profile.cover_photo_url} 
              alt="Cover" 
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <div className="text-6xl opacity-20">✨</div>
            </div>
          )}
        </div>

        {/* Consolidated Profile Info */}
        <div className="px-8 pb-6">
          {/* Top Row: Avatar + Stats + Action Button */}
          <div className="flex flex-col md:flex-row gap-6 -mt-16 mb-4">
            {/* Avatar */}
            <div className="flex-shrink-0">
              <Avatar className="h-32 w-32 border-4 border-white dark:border-dark-bg shadow-xl">
                <AvatarImage src={profile.avatar_url} />
                <AvatarFallback className="text-4xl bg-gradient-to-br from-primary/20 to-primary-dark/20 text-primary">
                  {profile.username?.[0]?.toUpperCase() || '?'}
                </AvatarFallback>
              </Avatar>
            </div>

            {/* Stats + Action Button */}
            <div className="flex-1 flex flex-col justify-end">
              {/* Stats Row - Above name */}
              <div className="flex items-center gap-6 mb-3 text-sm md:text-base flex-wrap">
                <div className="flex items-center gap-2">
                  <Code className="h-4 w-4 text-primary" />
                  <span className="font-bold text-text-dark dark:text-dark-text">{stats.skills}</span>
                  <span className="text-text-gray dark:text-dark-text-secondary">Skills</span>
                </div>
                <div className="flex items-center gap-2">
                  <Star className="h-4 w-4 text-primary" />
                  <span className="font-bold text-text-dark dark:text-dark-text">{stats.stars}</span>
                  <span className="text-text-gray dark:text-dark-text-secondary">Stars</span>
                </div>
                <div className="flex items-center gap-2">
                  <Download className="h-4 w-4 text-primary" />
                  <span className="font-bold text-text-dark dark:text-dark-text">{stats.installs}</span>
                  <span className="text-text-gray dark:text-dark-text-secondary">Downloads</span>
                </div>
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-primary" />
                  <span className="font-bold text-text-dark dark:text-dark-text">{profile.follower_count || 0}</span>
                  <span className="text-text-gray dark:text-dark-text-secondary">Followers</span>
                </div>
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-primary" />
                  <span className="font-bold text-text-dark dark:text-dark-text">{profile.following_count || 0}</span>
                  <span className="text-text-gray dark:text-dark-text-secondary">Following</span>
                </div>
              </div>
            </div>

            {/* Action Button - Right aligned */}
            <div className="flex items-end">
              {isOwnProfile ? (
                <Button
                  onClick={() => navigate('/settings')}
                  className="rounded-xl glass px-6"
                >
                  <Settings className="mr-2 h-4 w-4" />
                  Edit Profile
                </Button>
              ) : (
                <Button
                  onClick={handleFollow}
                  className={`rounded-xl px-6 ${isFollowing ? 'glass' : 'gradient-primary text-white'}`}
                >
                  {isFollowing ? 'Following' : 'Follow'}
                </Button>
              )}
            </div>
          </div>

          {/* Name and Handle */}
          <div className="mb-3">
            <h1 className="text-2xl font-manrope font-bold text-text-dark dark:text-dark-text mb-1">
              {profile.display_name || profile.username}
            </h1>
            <p className="text-base text-text-gray dark:text-dark-text-secondary">
              @{profile.username}
            </p>
          </div>
          
          {/* Bio */}
          {profile.bio && (
            <p className="text-text-gray dark:text-dark-text-secondary max-w-3xl mb-4">
              {profile.bio}
            </p>
          )}

          {/* Location & Social Links */}
          <div className="flex flex-wrap items-center gap-4 text-sm">
            {profile.location && (
              <div className="flex items-center gap-1.5 text-text-gray dark:text-dark-text-secondary">
                <MapPin className="h-4 w-4" />
                <span>{profile.location}</span>
              </div>
            )}
            
            {/* Social Links */}
            <div className="flex items-center gap-3 flex-wrap">
              {profile.website_url && (
                <a 
                  href={profile.website_url} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="flex items-center gap-1.5 text-text-gray dark:text-dark-text-secondary hover:text-primary transition-colors"
                  title={profile.website_url}
                >
                  <LinkIcon className="h-4 w-4" />
                  <span>{profile.website_url.replace(/^https?:\/\/(www\.)?/, '').split('/')[0]}</span>
                </a>
              )}
              {(profile.twitter_url || profile.twitter_handle) && (
                <a 
                  href={profile.twitter_url || `https://x.com/${profile.twitter_handle}`} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="flex items-center gap-1.5 text-text-gray dark:text-dark-text-secondary hover:text-primary transition-colors"
                  title="X (Twitter)"
                >
                  <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                  </svg>
                  <span>@{profile.twitter_handle || profile.twitter_url?.split('/').pop()}</span>
                </a>
              )}
              {profile.github_handle && (
                <a 
                  href={`https://github.com/${profile.github_handle}`} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="flex items-center gap-1.5 text-text-gray dark:text-dark-text-secondary hover:text-primary transition-colors"
                  title="GitHub"
                >
                  <Github className="h-4 w-4" />
                  <span>@{profile.github_handle}</span>
                </a>
              )}
              {profile.linkedin_url && (
                <a 
                  href={profile.linkedin_url} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="flex items-center gap-1.5 text-text-gray dark:text-dark-text-secondary hover:text-primary transition-colors"
                  title="LinkedIn"
                >
                  <Linkedin className="h-4 w-4" />
                  <span>LinkedIn</span>
                </a>
              )}
              {profile.substack_url && (
                <a 
                  href={profile.substack_url} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="flex items-center gap-1.5 text-text-gray dark:text-dark-text-secondary hover:text-primary transition-colors"
                  title="Substack"
                >
                  <BookOpen className="h-4 w-4" />
                  <span>Substack</span>
                </a>
              )}
              {profile.reddit_url && (
                <a 
                  href={profile.reddit_url} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="flex items-center gap-1.5 text-text-gray dark:text-dark-text-secondary hover:text-primary transition-colors"
                  title="Reddit"
                >
                  <MessageCircle className="h-4 w-4" />
                  <span>Reddit</span>
                </a>
              )}
            </div>
          </div>
        </div>
      </GlassCard>

      {/* Skills Section */}
      <div>
        <h2 className="text-2xl font-manrope font-bold text-text-dark dark:text-dark-text mb-6">
          Published Skills
        </h2>
        {skills.length === 0 ? (
          <GlassCard variant="strong" className="text-center py-16">
            <Code className="h-16 w-16 mx-auto mb-4 text-text-light dark:text-dark-text-secondary opacity-50" />
            <h3 className="text-xl font-semibold text-text-dark dark:text-dark-text mb-2">
              No skills yet
            </h3>
            <p className="text-text-gray dark:text-dark-text-secondary">
              {isOwnProfile ? 'Create your first skill to get started!' : 'This user hasn\'t published any skills yet.'}
            </p>
            {isOwnProfile && (
              <Button
                onClick={() => navigate('/builder')}
                className="gradient-primary text-white rounded-xl mt-6"
              >
                <Code className="mr-2 h-4 w-4" />
                Create Skill
              </Button>
            )}
          </GlassCard>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {skills.map((skill) => (
              <SkillCard
                key={skill.id}
                skill={skill}
                showActions={isOwnProfile}
                showSlug={false}
                showStatus={isOwnProfile}
                onEdit={handleEditSkill}
                onDelete={handleDeleteSkill}
                onCategoryChange={handleCategoryChange}
                onVisibilityChange={handleUpdateVisibility}
              />
            ))}
          </div>
        )}
      </div>

      {/* Category Modal */}
      {categoryModalOpen && (
        <CategoryModal
          isOpen={categoryModalOpen}
          onClose={() => {
            setCategoryModalOpen(false);
            setSkillToEditCategory(null);
          }}
          onSave={handleSaveCategory}
          currentCategory={skillToEditCategory?.category || skillToEditCategory?.category_id}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deleteModalOpen && (
        <ConfirmModal
          isOpen={deleteModalOpen}
          onClose={() => {
            setDeleteModalOpen(false);
            setSkillToDelete(null);
          }}
          onConfirm={confirmDelete}
          title="Delete Skill"
          message={`Are you sure you want to delete "${skillToDelete?.name}"? This action cannot be undone.`}
          confirmText="Delete"
          confirmVariant="destructive"
        />
      )}
    </div>
  );
};

export default ProfilePage;